import { RICH_TEXT_NODES, RICH_TEXT_THEME } from '@plato/shared-util/lexical-config';
import {
    RichTextInput as UIRichTextInput,
    type RichTextInputVariant,
    type RichTextValue,
} from '@plato/ui/components/rich-text-input';
import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { useControlledState } from '../../../lib/hooks/use-controlled-state.ts';
import type { AIActionType, AIWritingState } from '../hooks/use-improve-writing.ts';
import type { EntityType } from '../types/entity-types.ts';
import { AutoFocusPlugin } from './auto-focus-plugin.tsx';
import { ToolbarPlugin } from './toolbar-plugin.tsx';

export type { AIWritingState, RichTextInputVariant, RichTextValue };

export type RichTextInputProps = {
    disableVoiceRecording?: boolean;
    autoFocus?: boolean;
    entityId?: string;
    /** Entity type for improve-writing hook - enables AI actions when 'note' */
    entityType?: EntityType;
    value: RichTextValue | undefined;
    onChange: (value: RichTextValue) => void;
    placeholder?: string;
    /** Current AI writing state */
    aiWritingState?: AIWritingState;
    /** Callback when AI writing state changes */
    setAIWritingState?: Dispatch<SetStateAction<AIWritingState>>;
    onAIInteraction?: (action: AIActionType, sessionId: string) => void;
    className?: string;
    contentClassName?: string;
    error?: boolean;
    /**
     * `default`: full border and toolbar always visible.
     * `inline`: bottom-border-only placeholder look when unfocused; full border and
     * toolbar reveal on focus (driven by CSS `focus-within`, no JS state).
     */
    variant?: RichTextInputVariant;
};

export const RichTextInput = ({
    disableVoiceRecording,
    autoFocus = false,
    entityId,
    entityType,
    value,
    onChange,
    placeholder,
    aiWritingState: aiWritingStateProp,
    setAIWritingState: setAIWritingStateProp,
    onAIInteraction,
    className,
    contentClassName,
    error = false,
    variant = 'default',
}: RichTextInputProps): ReactElement => {
    const [aiWritingState, setAIWritingState] = useControlledState<AIWritingState>({
        state: aiWritingStateProp,
        setState: setAIWritingStateProp,
        defaultState: 'idle',
    });

    // Suspend onChange while AI is previewing a suggestion so the parent form
    // doesn't lock in the AI's draft until it's accepted.
    const gatedOnChange = aiWritingState === 'idle' ? onChange : undefined;

    return (
        <UIRichTextInput
            value={value}
            onChange={gatedOnChange}
            placeholder={placeholder}
            theme={RICH_TEXT_THEME}
            nodes={RICH_TEXT_NODES}
            namespace="RichTextViewer"
            variant={variant}
            error={error}
            className={className}
            contentClassName={contentClassName}
        >
            <ToolbarPlugin
                disableVoiceRecording={disableVoiceRecording}
                entityType={entityType}
                entityId={entityId}
                aiWritingState={aiWritingState}
                setAIWritingState={setAIWritingState}
                onChange={onChange}
                onAIInteraction={onAIInteraction}
                className={variant === 'inline' ? 'hidden group-focus-within:flex' : undefined}
            />
            {autoFocus && <AutoFocusPlugin />}
        </UIRichTextInput>
    );
};
